import { useCallback } from 'react';
import Media from 'react-media';

import { Column, Button, useTheme } from '@simplywallst/ui-core';
import ChartUpOutline from '@simplywallst/ui-core/icons/ChartUpOutline';
import SortingOutline from '@simplywallst/ui-core/icons/SortingOutline';
import SidebarOutline from '@simplywallst/ui-core/icons/SidebarOutline';

import { Dropdown } from '@/components/Dropdown';
import type { ButtonRenderProps } from '@/components/Dropdown';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import {
  WatchlistSorting,
  useWatchlistSorting,
} from '@/pages/Watchlist/hooks/useWatchlistSorting';
import { useWatchlistPeriod } from '@/pages/Watchlist/hooks/useWatchlistPeriod';
import type { HoldingsSorting } from '@/hooks/portfolio/useHoldingsSorting';
import { useHoldingsSorting } from '@/hooks/portfolio/useHoldingsSorting';
import { useTracking } from 'react-tracking';
import { usePortfolioPeriod } from '../../hooks';

export interface Props {
  onToggleTableExpansion: () => void;
}

export const periodLabelsFor = [
  { label: '1M', option: '1M' },
  { label: '3M', option: '3M' },
  { label: '1Y', option: '1Y' },
];

const sortingOptions: {
  option: WatchlistSorting;
  label: string;
}[] = [
  {
    option: WatchlistSorting.PERFORMANCE_7D,
    label: '7D Performance',
  },
  {
    option: WatchlistSorting.RECENTLY_ADDED,
    label: 'Recently added',
  },
  {
    option: WatchlistSorting.TOTAL_SCORE_HIGH_TO_LOW,
    label: 'Total score',
  },
  {
    option: WatchlistSorting.VALUE_SCORE_HIGH_TO_LOW,
    label: 'Value score',
  },
  {
    option: WatchlistSorting.FUTURE_SCORE_HIGH_TO_LOW,
    label: 'Future score',
  },
  {
    option: WatchlistSorting.PAST_SCORE_HIGH_TO_LOW,
    label: 'Past score',
  },
  {
    option: WatchlistSorting.DIVIDEND_SCORE_HIGH_TO_LOW,
    label: 'Dividend score',
  },
  {
    option: WatchlistSorting.HEALTH_SCORE_HIGH_TO_LOW,
    label: 'Health score',
  },
  {
    option: WatchlistSorting.TICKER_A_TO_Z,
    label: 'Ticker A - Z',
  },
  {
    option: WatchlistSorting.COMPANY_NAME_A_TO_Z,
    label: 'Name A - Z',
  },
  {
    option: WatchlistSorting.MARKET_CAP_HIGH_TO_LOW,
    label: 'Market Cap',
  },
  {
    option: WatchlistSorting.DISCOUNT_HIGH_TO_LOW,
    label: 'Most Undervalued',
  },
  {
    option: WatchlistSorting.DISCOUNT_LOW_TO_HIGH,
    label: 'Most Overvalued',
  },
];

export const TableActions = ({ onToggleTableExpansion }: Props) => {
  const theme = useTheme();
  const { isDesktop } = useUserAgent();

  const [watchlistPeriod, setWatchlistPeriod] = useWatchlistPeriod();
  const [watchlistSorting, setWatchlistSorting] = useWatchlistSorting();

  const periodButton = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
    }: ButtonRenderProps) => {
      return (
        <Button
          styleType="passive"
          alignIcon="left"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          data-cy-id={name}
        >
          <ChartUpOutline aria-label="Period Select" />
          {value}
        </Button>
      );
    },
    []
  );

  const sortingButton = useCallback(
    ({ onBlur, onFocus, onClick, innerRef, name }: ButtonRenderProps) => {
      const sortingLabel =
        sortingOptions.find((o) => o.option === watchlistSorting)?.label ||
        'Sort';
      return (
        <Button
          styleType="passive"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          data-cy-id={name}
          alignIcon="left"
          aria-label="Sort Holdings"
        >
          <SortingOutline /> {sortingLabel}
        </Button>
      );
    },
    [watchlistSorting]
  );

  const handlePeriodSelect = (value: string) => {
    setWatchlistPeriod(value as typeof watchlistPeriod);
  };

  const handleSortingSelect = (value: string) => {
    setWatchlistSorting(value as typeof watchlistSorting);
  };

  return (
    <Column
      displayFlex
      alignItem="center"
      width="auto"
      data-cy-id="watchlist-header-buttons"
    >
      <Dropdown
        name="period-selection"
        align="right"
        displayType="inline-block"
        options={periodLabelsFor}
        buttonRender={periodButton}
        value={watchlistPeriod}
        onSelect={handlePeriodSelect}
      />
      <Dropdown
        name="sorting-selection"
        align="right"
        displayType="inline-block"
        options={sortingOptions}
        buttonRender={sortingButton}
        value={watchlistSorting}
        onSelect={handleSortingSelect}
      />
      <Media query={{ minWidth: theme.lgMinRaw }} defaultMatches={isDesktop}>
        <Button styleType="passive">
          <SidebarOutline
            aria-label="Expand Watchlist Table"
            onClick={onToggleTableExpansion}
          />
        </Button>
      </Media>
    </Column>
  );
};

const PortfolioSortingOptions: { option: HoldingsSorting; label: string }[] = [
  {
    option: 'performance-7d-desc',
    label: '7D Performance',
  },
  {
    option: 'value-desc',
    label: 'Total Value',
  },
  {
    option: 'cash-return-desc',
    label: 'Cash return',
  },
  {
    option: 'roi-desc',
    label: 'ROI',
  },
  {
    option: 'total-score-desc',
    label: 'Total score',
  },
  {
    option: 'value-score-desc',
    label: 'Value score',
  },
  {
    option: 'future-score-desc',
    label: 'Future score',
  },
  {
    option: 'past-score-desc',
    label: 'Past score',
  },
  {
    option: 'dividend-score-desc',
    label: 'Dividend score',
  },
  {
    option: 'health-score-desc',
    label: 'Health score',
  },
  {
    option: 'ticker-a-to-z',
    label: 'Ticker A - Z',
  },
  {
    option: 'company-name-a-to-z',
    label: 'Name A - Z',
  },
  {
    option: 'market-cap-desc',
    label: 'Market Cap',
  },
  {
    option: 'discount-desc',
    label: 'Most Undervalued',
  },
  {
    option: 'discount-asc',
    label: 'Most Overvalued',
  },
];

export const PortfolioTableActions = ({ onToggleTableExpansion }: Props) => {
  const theme = useTheme();
  const { isDesktop } = useUserAgent();
  const { trackEvent } = useTracking();

  const [value, setHoldingsSorting] = useHoldingsSorting();
  const [portfolioPeriod, setPortfolioPeriod] = usePortfolioPeriod();

  const periodButton = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
    }: ButtonRenderProps) => {
      return (
        <Button
          styleType="passive"
          alignIcon="left"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          data-cy-id={name}
          aria-label="Period Select"
        >
          <ChartUpOutline />
          {value}
        </Button>
      );
    },
    []
  );

  const sortingButton = useCallback(
    ({ onBlur, onFocus, onClick, innerRef, name }: ButtonRenderProps) => {
      const sortingLabel =
        PortfolioSortingOptions.find((o) => o.option === value)?.label ||
        'Sort';
      return (
        <Button
          styleType="passive"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          data-cy-id={name}
          alignIcon="left"
          aria-label="Sort Holdings"
        >
          <SortingOutline /> {sortingLabel}
        </Button>
      );
    },
    [value]
  );

  const handlePeriodSelect = (value: string) => {
    setPortfolioPeriod(value as typeof portfolioPeriod);
  };

  const handleSelect = (o: string) => {
    setHoldingsSorting(o as HoldingsSorting);
    trackEvent({ modifier: 'select' });
  };

  return (
    <Column
      displayFlex
      alignItem="center"
      width="auto"
      data-testid="header-buttons"
    >
      <Dropdown
        name="period-selection"
        align="right"
        displayType="inline-block"
        options={periodLabelsFor}
        buttonRender={periodButton}
        value={portfolioPeriod}
        onSelect={handlePeriodSelect}
      />
      <Dropdown
        name="sorting-selection"
        align="right"
        displayType="inline-block"
        options={PortfolioSortingOptions}
        buttonRender={sortingButton}
        value={value}
        onSelect={handleSelect}
      />
      <Media query={{ minWidth: theme.lgMinRaw }} defaultMatches={isDesktop}>
        <Button styleType="passive">
          <SidebarOutline
            aria-label="Expand Portfolio Table"
            onClick={onToggleTableExpansion}
          />
        </Button>
      </Media>
    </Column>
  );
};
